import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, CalendarDaysIcon, SparklesIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { bookingsAPI } from '../utils/api';
import toast from 'react-hot-toast';

// Niveaux d'urgence de l'application (reservation_sheet.dart)
const URGENCES = ['Normal', 'Urgent', 'Très urgent'];

// Heure retenue pour chaque créneau, afin de produire une `dateIntervention`
const SLOT_TIMES = {
  'Matin (8h-12h)': '09:00',
  'Après-midi (12h-17h)': '14:00',
  'Soir (17h-21h)': '18:00',
};

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const serviceName = searchParams.get('service') || 'Service';
  const category = searchParams.get('category') || '';
  
  const [step, setStep] = useState(1);
  const [location, setLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState({
    description: '',
    date: '',
    timeSlot: '',
    budget: '',
    urgence: 'Normal',
    address: '',
    service: serviceName,
    category: category,
    coordinates: null
  });

  // Reverse geocoding
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      const data = await response.json();
      if (data && data.display_name) {
        return data.display_name;
      }
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch (error) {
      console.error('Erreur reverse geocoding:', error);
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  }, []);

  // Géolocalisation
  const getCurrentLocation = useCallback(() => {
    setLoadingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setLocation(coords);
          
          const address = await reverseGeocode(coords.lat, coords.lng);
          
          setBooking(prev => ({
            ...prev,
            address: address,
            coordinates: coords
          }));
          setLoadingLocation(false);
          toast.success('Position détectée avec succès');
        },
        (error) => {
          console.error('Erreur géolocalisation:', error);
          setLoadingLocation(false);
          toast.error('Impossible de détecter votre position. Veuillez entrer votre adresse manuellement.');
        }
      );
    } else {
      setLoadingLocation(false);
      toast.error('Géolocalisation non supportée par votre navigateur');
    }
  }, [reverseGeocode]);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Veuillez vous connecter pour effectuer une réservation');
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    getCurrentLocation();
  }, [getCurrentLocation]);

  const steps = [
    { number: 1, title: 'Détails', icon: SparklesIcon },
    { number: 2, title: 'Date', icon: CalendarDaysIcon },
    { number: 3, title: 'Localisation', icon: MapPinIcon },
    { number: 4, title: 'Confirmation', icon: CheckCircleIcon },
  ];

  const nextStep = () => setStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
  if (!user) {
    toast.error('Veuillez vous connecter');
    navigate('/login');
    return;
  }

  if (!booking.description.trim()) {
    toast.error('Veuillez décrire votre besoin');
    return;
  }
  if (!booking.date) {
    toast.error('Veuillez sélectionner une date');
    return;
  }
  if (!booking.timeSlot) {
    toast.error('Veuillez sélectionner un créneau horaire');
    return;
  }
  if (!booking.address.trim()) {
    toast.error('Veuillez indiquer votre adresse');
    return;
  }

  setSubmitting(true);

  // Le créneau choisi devient une heure concrète : l'application affiche
  // `dateIntervention` comme une date et heure, pas comme un libellé.
  const dateIntervention = new Date(`${booking.date}T${SLOT_TIMES[booking.timeSlot] || '09:00'}:00`);

  const payload = {
    // Le service choisi n'a pas de champ dédié dans `reservations` :
    // il est repris en tête de description, comme le fait l'appli.
    description: `${booking.service} — ${booking.description}`,
    adresse: booking.address,
    latitude: booking.coordinates?.lat ?? null,
    longitude: booking.coordinates?.lng ?? null,
    budget: booking.budget,
    urgence: booking.urgence,
    dateIntervention: dateIntervention.toISOString(),
  };

  try {
    await bookingsAPI.create(payload);

    toast.success('Votre demande a été envoyée avec succès !');
    navigate('/dashboard');
  } catch (error) {
    console.error('Erreur création booking:', error);
    toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi de la demande');
  } finally {
    setSubmitting(false);
  }
};

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12">
      <div className="container-custom max-w-3xl">
        {/* Progress bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            {steps.map((s) => (
              <div key={s.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                    step >= s.number 
                      ? 'bg-[#F97316] text-white shadow-lg shadow-[#F97316]/25' 
                      : 'bg-[#111111] text-gray-500 border border-[#252525]'
                  }`}>
                    {step > s.number ? <CheckCircleIcon className="h-5 w-5" /> : s.icon ? <s.icon className="h-5 w-5" /> : s.number}
                  </div>
                  <span className={`text-xs mt-2 ${step >= s.number ? 'text-[#F97316]' : 'text-gray-600'}`}>
                    {s.title}
                  </span>
                </div>
                {s.number < 4 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    step > s.number ? 'bg-[#F97316]' : 'bg-[#252525]'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contenu des étapes - garder le reste inchangé */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-[#111111] rounded-2xl border border-[#252525] p-8"
          >
            {/* Étape 1 : Description */}
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-semibold mb-6 text-white">
                  Décrivez votre besoin : <span className="text-[#F97316]">{booking.service}</span>
                </h2>
                <textarea
                  value={booking.description}
                  onChange={(e) => setBooking({...booking, description: e.target.value})}
                  rows={6}
                  className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white 
                             placeholder-gray-500 focus:ring-2 focus:ring-[#F97316] focus:border-transparent resize-none"
                  placeholder="Décrivez précisément ce dont vous avez besoin..."
                />
                <div className="mt-4 p-4 bg-[#0a0a0a] rounded-xl border border-[#252525]">
                  <p className="text-sm text-gray-400">
                    💡 <span className="text-[#F97316]">Astuce :</span> Plus vous êtes précis, mieux nous pourrons vous trouver le Smart idéal !
                  </p>
                </div>
              </div>
            )}

            {/* Étape 2 : Date */}
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-semibold mb-6 text-white">Choisissez votre date et horaire</h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#F97316] mb-2">Date souhaitée</label>
                    <input
                      type="date"
                      value={booking.date}
                      onChange={(e) => setBooking({...booking, date: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white 
                                 focus:ring-2 focus:ring-[#F97316] focus:border-transparent custom-date-input"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#F97316] mb-2">Créneau horaire</label>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.keys(SLOT_TIMES).map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setBooking({...booking, timeSlot: slot})}
                          className={`p-3 rounded-xl text-sm font-medium transition-all ${
                            booking.timeSlot === slot
                              ? 'bg-[#F97316] text-white'
                              : 'bg-[#0a0a0a] text-gray-400 border border-[#252525] hover:border-[#F97316]/50'
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Budget et urgence : attendus par l'application mobile,
                      qui les affiche au Smart avec la demande */}
                  <div>
                    <label className="block text-sm font-medium text-[#F97316] mb-2">
                      Budget indicatif (FCFA)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={booking.budget}
                      onChange={(e) => setBooking({...booking, budget: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white
                                 placeholder-gray-500 focus:ring-2 focus:ring-[#F97316] focus:border-transparent"
                      placeholder="5000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F97316] mb-2">
                      Niveau d'urgence
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {URGENCES.map((niveau) => (
                        <button
                          key={niveau}
                          type="button"
                          onClick={() => setBooking({...booking, urgence: niveau})}
                          className={`p-3 rounded-xl text-sm font-medium transition-all ${
                            booking.urgence === niveau
                              ? 'bg-[#F97316] text-white'
                              : 'bg-[#0a0a0a] text-gray-400 border border-[#252525] hover:border-[#F97316]/50'
                          }`}
                        >
                          {niveau}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Étape 3 : Localisation */}
            {step === 3 && (
              <div>
                <h2 className="text-2xl font-semibold mb-6 text-white">Où intervenons-nous ?</h2>
                
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  disabled={loadingLocation}
                  className="w-full p-4 bg-[#0a0a0a] rounded-xl border border-[#252525] 
                             hover:border-[#F97316]/50 transition-all mb-4 flex items-center justify-center space-x-3"
                >
                  {loadingLocation ? (
                    <ArrowPathIcon className="h-5 w-5 text-[#F97316] animate-spin" />
                  ) : (
                    <MapPinIcon className="h-5 w-5 text-[#F97316]" />
                  )}
                  <span className="text-white">
                    {loadingLocation ? 'Détection en cours...' : '📍 Utiliser ma position actuelle'}
                  </span>
                </button>

                {location && (
                  <div className="p-4 bg-[#F97316]/10 rounded-xl border border-[#F97316]/30 mb-4">
                    <p className="text-[#F97316] text-sm flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4" />
                      Position détectée
                    </p>
                    <p className="text-gray-300 text-sm mt-1 truncate">{booking.address}</p>
                  </div>
                )}

                <div className="relative">
                  <MapPinIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <input
                    type="text"
                    value={booking.address}
                    onChange={(e) => setBooking({...booking, address: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#0a0a0a] border border-[#252525] text-white 
                               placeholder-gray-500 focus:ring-2 focus:ring-[#F97316] focus:border-transparent"
                    placeholder="Ou entrez votre adresse manuellement..."
                  />
                </div>
              </div>
            )}

            {/* Étape 4 : Confirmation */}
            {step === 4 && (
              <div>
                <h2 className="text-2xl font-semibold mb-6 text-white">Confirmation de votre demande</h2>
                
                <div className="space-y-4 bg-[#0a0a0a] rounded-xl p-5">
                  <div className="flex justify-between py-2 border-b border-[#252525]">
                    <span className="text-gray-400">Service :</span>
                    <span className="text-white font-medium">{booking.service}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#252525]">
                    <span className="text-gray-400">Date :</span>
                    <span className="text-white">{booking.date || 'Non renseignée'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#252525]">
                    <span className="text-gray-400">Créneau :</span>
                    <span className="text-white">{booking.timeSlot || 'Non renseigné'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#252525]">
                    <span className="text-gray-400">Budget :</span>
                    <span className="text-white">
                      {booking.budget ? `${booking.budget} FCFA` : 'Non renseigné'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#252525]">
                    <span className="text-gray-400">Urgence :</span>
                    <span className="text-white">{booking.urgence}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#252525]">
                    <span className="text-gray-400">Adresse :</span>
                    <span className="text-white truncate max-w-[200px]">
                      {typeof booking.address === 'string' 
                        ? booking.address 
                        : booking.address?.street || 'Adresse non renseignée'}
                    </span>
                  </div>
                  <div className="py-2">
                    <span className="text-gray-400 block mb-2">Description :</span>
                    <p className="text-white text-sm bg-[#111111] p-3 rounded-lg">
                      {booking.description || 'Aucune description'}
                    </p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-[#F97316]/10 rounded-xl border border-[#F97316]/30">
                  <p className="text-[#F97316] text-sm flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4" />
                    Une fois confirmée, votre demande sera envoyée à notre équipe.
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    Vous recevrez une confirmation par email et pourrez suivre l'évolution dans votre tableau de bord.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#252525]">
              {step > 1 && (
                <button onClick={prevStep} className="px-6 py-2 rounded-xl bg-[#1a1a1a] text-white hover:bg-[#252525] transition">
                  ← Retour
                </button>
              )}
              {step < 4 ? (
                <button onClick={nextStep} className="btn-primary ml-auto">
                  Continuer →
                </button>
              ) : (
                <button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  className="btn-primary ml-auto flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    'Confirmer ma demande →'
                  )}
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookingPage;